import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { attachmentsApi, shopifyApi } from '@/api/shopify.api';
import { Field } from '@/components/ui/Field';
import {
  CATEGORY_CODES,
  CATEGORY_LABEL,
  CustomerMood,
  URGENCY_FLAGS,
  URGENCY_FLAG_LABELS
} from '@/types/domain';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { useStores } from '@/hooks/useStores';
import type {
  CreateTicketAttachmentInline,
  CreateTicketPayload,
  UploadedAttachment
} from '@/types/api';

const TicketSchema = z.object({
  category: z.enum(CATEGORY_CODES),
  // V4.9.10 — Required when category='B1'. Cross-field validation
  // applied in onSubmit so the form shows a friendly error message.
  quotation_type: z
    .enum(['Standard', 'Bulk', 'Trade show booth kit'])
    .optional(),
  store_code: z.string().min(2, 'Select a store').max(10, 'Invalid store code'),
  order_number: z
    .string()
    .trim()
    .max(50, 'Order number is too long')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  customer_name: z.string().trim().min(1, 'Customer name is required').max(200),
  customer_email: z
    .string()
    .trim()
    .email('Enter a valid email')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  customer_mood: z.nativeEnum(CustomerMood).optional(),
  summary: z.string().trim().min(3, 'Summary must be at least 3 characters').max(5000),
  requested_action: z
    .string()
    .max(5000)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  event_date: z
    .string()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  urgency_flags: z.array(z.string()).optional(),
  force_create: z.boolean().optional()
});

type TicketFormValues = z.infer<typeof TicketSchema>;

interface StagedAttachment extends UploadedAttachment {
  id: string;
}

export function NewTicketPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const [apiError, setApiError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ id: string } | null>(null);
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'found' | 'missing' | 'error'>('idle');
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [shopifyAdminUrl, setShopifyAdminUrl] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const storesQuery = useStores();
  const lookupTimer = useRef<number | null>(null);
  const lookupSeq = useRef(0);
  // V4.7 — B-category draft order has two modes:
  //   'has'  → admin has a real Shopify draft ID; show text input + autofill
  //   'none' → no draft yet (99% of B-category cases); hide input, allow save
  // Reset whenever category changes from B to A or vice versa.
  const [draftMode, setDraftMode] = useState<'has' | 'none'>('has');
  // V4.9 — Event-date selector follows the same pattern: explicit choice
  // first, calendar input only appears when "Yes, has event date".
  const [eventDateMode, setEventDateMode] = useState<'yes' | 'no'>('no');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    getValues
  } = useForm<TicketFormValues>({
    resolver: zodResolver(TicketSchema),
    defaultValues: {
      category: 'A2',
      store_code: '',
      customer_name: '',
      summary: '',
      customer_mood: CustomerMood.Calm,
      urgency_flags: []
    }
  });

  useEffect(() => {
    const stores = storesQuery.data;
    if (stores && stores.length > 0 && !getValues('store_code')) {
      setValue('store_code', stores[0].code, { shouldValidate: false });
    }
  }, [storesQuery.data, getValues, setValue]);

  const orderNumberValue = watch('order_number');
  const storeCodeValue = watch('store_code');
  const categoryValue = watch('category');
  // V4.4 — Family-aware autofill. A1-A7 → real Shopify Order;
  // B1-B5 → Shopify Draft Order. Same input field, same response shape;
  // only the API endpoint and the field label differ.
  const isSalesCategory = (categoryValue ?? '').toString().toUpperCase().startsWith('B');

  // Debounced Shopify order / draft-order lookup.
  useEffect(() => {
    if (lookupTimer.current) {
      window.clearTimeout(lookupTimer.current);
      lookupTimer.current = null;
    }
    const trimmed = orderNumberValue?.trim();
    // V4.7 — Skip lookup entirely when admin chose "No draft order ID
    // available" on a B-category ticket; that's an explicit "no Shopify
    // reference" choice, not a missing field.
    if (isSalesCategory && draftMode === 'none') {
      setLookupStatus('idle');
      setLookupMessage(null);
      setShopifyAdminUrl(null);
      return;
    }
    if (!trimmed || !storeCodeValue) {
      setLookupStatus('idle');
      setLookupMessage(null);
      setShopifyAdminUrl(null);
      return;
    }
    const seq = ++lookupSeq.current;
    setLookupStatus('loading');
    setLookupMessage(null);
    lookupTimer.current = window.setTimeout(async () => {
      try {
        const result = isSalesCategory
          ? await shopifyApi.lookupDraftOrder(storeCodeValue, trimmed)
          : await shopifyApi.lookupOrder(storeCodeValue, trimmed);
        if (seq !== lookupSeq.current) return;
        setShopifyAdminUrl(result.shopify_admin_url);
        if (result.found && result.order) {
          setLookupStatus('found');
          setLookupMessage(
            result.live
              ? isSalesCategory
                ? `Matched Shopify draft order ${result.order.name ?? trimmed}`
                : `Matched Shopify order ${result.order.name ?? trimmed}`
              : 'Matched (cached)'
          );
          if (result.order.customer_name) {
            setValue('customer_name', result.order.customer_name, { shouldValidate: true });
          }
          if (result.order.email) {
            setValue('customer_email', result.order.email, { shouldValidate: true });
          }
        } else {
          setLookupStatus('missing');
          setLookupMessage(
            result.message ??
              (isSalesCategory ? 'Draft order not found in Shopify.' : 'Order not found in Shopify.')
          );
        }
      } catch (error) {
        if (seq !== lookupSeq.current) return;
        setLookupStatus('error');
        setLookupMessage(
          error instanceof ApiError
            ? error.problem?.detail ?? error.message
            : 'Shopify lookup failed.'
        );
      }
    }, 450);
    return () => {
      if (lookupTimer.current) {
        window.clearTimeout(lookupTimer.current);
        lookupTimer.current = null;
      }
    };
  }, [orderNumberValue, storeCodeValue, isSalesCategory, draftMode, setValue]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateTicketPayload) => ticketsApi.create(payload),
    onSuccess: (ticket) => {
      push({ kind: 'success', title: 'Ticket created', message: `Ticket ${ticket.id} created` });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const problem = error.problem;
        const detail = problem?.detail ?? '';
        if (
          problem?.type?.includes('duplicate') ||
          detail.toUpperCase().includes('DUPLICATE_TICKET') ||
          detail.toLowerCase().includes('already has open ticket')
        ) {
          const match = detail.match(/ticket\s+(BDS-\d+)/i);
          if (match) {
            setDuplicate({ id: match[1] });
          }
        }
        setApiError(detail || error.message);
      } else {
        setApiError('Ticket creation failed');
      }
    }
  });

  const onSubmit = handleSubmit((values) => {
    setApiError(null);
    setDuplicate(null);

    // V4.9.10 — Cross-field validation: B1 requires quotation_type.
    // (Zod doesn't easily express "required iff sibling=X" with refine(),
    // and the backend validates anyway, but catching it client-side is
    // friendlier and avoids a roundtrip.)
    if (values.category === 'B1' && !values.quotation_type) {
      setApiError('Please choose a Quotation type (Standard, Bulk, or Trade show booth kit) before submitting.');
      return;
    }

    const payload: CreateTicketPayload = {
      category: values.category,
      store_code: values.store_code,
      customer_name: values.customer_name,
      summary: values.summary,
      order_number: values.order_number,
      customer_email: values.customer_email,
      customer_mood: values.customer_mood,
      event_date: values.event_date,
      requested_action: values.requested_action,
      urgency_flags: values.urgency_flags,
      force_create: values.force_create,
      // V4.9.10 — Include quotation_type only when category=B1.
      quotation_type: values.category === 'B1' ? values.quotation_type : undefined,
      attachments: attachments.length
        ? attachments.map<CreateTicketAttachmentInline>((a) => ({
            filename: a.filename,
            s3_key: a.s3_key,
            mime_type: a.mime_type ?? undefined,
            size_bytes: a.size_bytes
          }))
        : undefined
    };
    createMutation.mutate(payload);
  });

  const urgencyFlags = watch('urgency_flags') ?? [];
  const toggleFlag = (flag: string) => {
    const current = new Set(getValues('urgency_flags') ?? []);
    if (current.has(flag)) current.delete(flag);
    else current.add(flag);
    setValue('urgency_flags', Array.from(current), { shouldDirty: true });
  };

  const handleFilesSelected = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setUploading(true);
    const files = Array.from(list);
    try {
      for (const file of files) {
        const result = await attachmentsApi.upload(file);
        setAttachments((prev) => [
          ...prev,
          { ...result, id: `${result.s3_key}-${Date.now()}-${Math.random()}` }
        ]);
        push({ kind: 'success', title: 'File uploaded', message: result.filename });
      }
    } catch (error) {
      push({
        kind: 'error',
        title: 'Upload failed',
        message: error instanceof ApiError ? error.problem?.detail ?? error.message : 'Upload failed'
      });
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <div className="form-page">
      <div className="section-header">
        <h2>Create ticket</h2>
        <p className="text-muted text-small" style={{ margin: '4px 0 0' }}>
          Raise a new support ticket. Fields marked * are required.
        </p>
      </div>
      <form onSubmit={onSubmit} noValidate>
        <div className="card mb-4">
          <div className="card-header"><h3 className="card-title">Ticket &amp; store</h3></div>
          <div className="card-body">
            <div className="form-grid">
              <Field label="Category" htmlFor="category" required error={errors.category?.message ?? null}>
                <select id="category" className="form-select" {...register('category')}>
                  {CATEGORY_CODES.map((code) => (
                    <option key={code} value={code}>
                      {code} · {CATEGORY_LABEL[code]}
                    </option>
                  ))}
                </select>
              </Field>

              {/* V4.9.10 — Quotation sub-type — REQUIRED for B1 tickets.
                 Bulk and Trade show booth kit also trigger an auto-assign
                 owner-override so the ticket lands in Idris's My Queue. */}
              {categoryValue === 'B1' ? (
                <Field
                  label="Quotation type"
                  htmlFor="quotation_type"
                  required
                  error={errors.quotation_type?.message ?? null}
                  hint="Bulk and Trade show booth kit go to Idris's queue. Standard follows normal routing."
                >
                  <select
                    id="quotation_type"
                    className="form-select"
                    {...register('quotation_type')}
                  >
                    <option value="">Select a quotation type...</option>
                    <option value="Standard">Standard</option>
                    <option value="Bulk">Bulk</option>
                    <option value="Trade show booth kit">Trade show booth kit</option>
                  </select>
                </Field>
              ) : null}

              <Field
                label="Store"
                htmlFor="store_code"
                required
                error={errors.store_code?.message ?? null}
                hint={
                  storesQuery.isError
                    ? 'Could not load store list from backend.'
                    : 'Sourced from /api/v1/shopify/stores'
                }
              >
                <select
                  id="store_code"
                  className="form-select"
                  disabled={storesQuery.isLoading}
                  {...register('store_code')}
                >
                  {storesQuery.isLoading ? (
                    <option value="">Loading stores...</option>
                  ) : (
                    <>
                      <option value="">Select a store</option>
                      {(storesQuery.data ?? []).map((store) => (
                        <option key={store.code} value={store.code}>
                          {store.code} · {store.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </Field>

              {isSalesCategory ? (
                <Field
                  label="Draft order ID"
                  htmlFor="order_number"
                  hint={
                    draftMode === 'has'
                      ? renderLookupHint(lookupStatus, lookupMessage, shopifyAdminUrl, true)
                      : 'No draft order — ticket will be created without a Shopify reference.'
                  }
                  error={errors.order_number?.message ?? null}
                >
                  {/* V4.7 — Two-step UX: admin picks "has" or "none" first. */}
                  <select
                    className="form-select"
                    value={draftMode}
                    style={{ marginBottom: 8 }}
                    onChange={(e) => {
                      const next = e.target.value as 'has' | 'none';
                      setDraftMode(next);
                      if (next === 'none') {
                        // Clear any partial value so submission is clean.
                        setValue('order_number', undefined, { shouldValidate: false });
                        setLookupStatus('idle');
                        setLookupMessage(null);
                        setShopifyAdminUrl(null);
                      }
                    }}
                  >
                    <option value="has">I have a draft order ID</option>
                    <option value="none">No draft order ID available</option>
                  </select>
                  {draftMode === 'has' ? (
                    <input
                      id="order_number"
                      className="form-input"
                      placeholder="e.g. D5 or #D5 (Shopify draft order)"
                      {...register('order_number')}
                    />
                  ) : null}
                </Field>
              ) : (
                <Field
                  label="Order number"
                  htmlFor="order_number"
                  hint={renderLookupHint(lookupStatus, lookupMessage, shopifyAdminUrl, false)}
                  error={errors.order_number?.message ?? null}
                >
                  <input
                    id="order_number"
                    className="form-input"
                    placeholder="e.g. 1234 or #1234"
                    {...register('order_number')}
                  />
                </Field>
              )}
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header"><h3 className="card-title">Customer</h3></div>
          <div className="card-body">
            <div className="form-grid">
              <Field
                label="Customer name"
                htmlFor="customer_name"
                required
                error={errors.customer_name?.message ?? null}
              >
                <input id="customer_name" className="form-input" {...register('customer_name')} />
              </Field>

              <Field
                label="Customer email"
                htmlFor="customer_email"
                error={errors.customer_email?.message ?? null}
              >
                <input
                  id="customer_email"
                  type="email"
                  className="form-input"
                  {...register('customer_email')}
                />
              </Field>

              <Field
                label="Customer mood"
                htmlFor="customer_mood"
                error={errors.customer_mood?.message ?? null}
              >
                <select id="customer_mood" className="form-select" {...register('customer_mood')}>
                  {Object.values(CustomerMood).map((mood) => (
                    <option key={mood} value={mood}>
                      {mood}
                    </option>
                  ))}
                </select>
              </Field>

              {/* V4.9 — Two-step event date selector. Most tickets don't
                  have a fixed event date, so we ask explicitly first to
                  avoid a always-visible empty calendar input. */}
              <Field label="Event date" htmlFor="event_date_mode" error={errors.event_date?.message ?? null}>
                <select
                  id="event_date_mode"
                  className="form-select"
                  value={eventDateMode}
                  onChange={(e) => {
                    const next = e.target.value as 'yes' | 'no';
                    setEventDateMode(next);
                    if (next === 'no') {
                      // Clear any previously-entered date so the backend
                      // doesn't receive a stale value.
                      setValue('event_date', '', { shouldValidate: false });
                    }
                  }}
                >
                  <option value="no">No event date / not applicable</option>
                  <option value="yes">Yes — has an event date</option>
                </select>
                {eventDateMode === 'yes' ? (
                  <input
                    id="event_date"
                    type="date"
                    className="form-input"
                    style={{ marginTop: 8 }}
                    {...register('event_date')}
                  />
                ) : null}
              </Field>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">Details &amp; attachments</h3></div>
          <div className="card-body">
            <Field label="Summary" htmlFor="summary" required error={errors.summary?.message ?? null}>
              <textarea id="summary" className="form-textarea" rows={3} {...register('summary')} />
            </Field>

            <Field
              label="Requested action (optional)"
              htmlFor="requested_action"
              error={errors.requested_action?.message ?? null}
            >
              <textarea
                id="requested_action"
                className="form-textarea"
                rows={2}
                {...register('requested_action')}
              />
            </Field>

            <Field label="Urgency flags">
              <div className="flex wrap gap-2">
                {URGENCY_FLAGS.map((flag) => {
                  const checked = urgencyFlags.includes(flag);
                  return (
                    <button
                      type="button"
                      key={flag}
                      className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleFlag(flag)}
                    >
                      {URGENCY_FLAG_LABELS[flag] ?? flag}
                    </button>
                  );
                })}
              </div>
              <div className="form-hint">Severity is computed automatically from these flags (P1/P2/P3).</div>
            </Field>

            {/* V4.9 — Attachments section redesigned as a proper card with
                a custom button. The default <input type="file"> looks
                browser-default ugly; we hide it and use a styled label. */}
            <Field label="Attachments (optional)">
              <div className="attachment-zone">
                <input
                  id="attachment-input"
                  type="file"
                  multiple
                  disabled={uploading}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    void handleFilesSelected(e.target.files);
                    e.target.value = '';
                  }}
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                />
                <label htmlFor="attachment-input" className="attachment-zone-cta">
                  <div className="attachment-zone-icon">📎</div>
                  <div>
                    <div className="attachment-zone-title">
                      {uploading ? 'Uploading…' : 'Choose files to attach'}
                    </div>
                    <div className="attachment-zone-sub">
                      Images, PDFs, documents, or videos · up to <strong>1 GB each</strong>
                    </div>
                  </div>
                </label>
              </div>
              {attachments.length > 0 ? (
                <ul className="attachment-list">
                  {attachments.map((a) => {
                    const sizeMb = a.size_bytes / (1024 * 1024);
                    const sizeDisplay =
                      sizeMb >= 1
                        ? `${sizeMb.toFixed(1)} MB`
                        : `${(a.size_bytes / 1024).toFixed(1)} KB`;
                    return (
                      <li key={a.id} className="attachment-item">
                        <div className="attachment-item-meta">
                          <div className="attachment-item-name">{a.filename}</div>
                          <div className="text-small text-muted">
                            {sizeDisplay} · {a.mime_type ?? 'unknown type'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => removeAttachment(a.id)}
                          title="Remove this attachment"
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </Field>

            {/* V4.9 — Override-duplicate moved into a soft warning card so
                admins know it's an "are you sure?" action, not a casual toggle. */}
            <Field label="Duplicate order check">
              <label className="override-duplicate">
                <input type="checkbox" {...register('force_create')} />
                <div>
                  <div className="override-duplicate-title">
                    Force create even if an open ticket exists for this order
                  </div>
                  <div className="text-small text-muted">
                    By default, raising a second ticket for an order that already has an open ticket is
                    blocked to prevent duplicate work. Tick this only if you've checked and a separate
                    ticket really is needed.
                  </div>
                </div>
              </label>
            </Field>

            {duplicate ? (
              <div className="form-error">
                An open ticket already exists for this order:{' '}
                <a href={`/tickets/${duplicate.id}`}>{duplicate.id}</a>. Tick the checkbox above to force
                create a new ticket anyway.
              </div>
            ) : null}
            {apiError ? <div className="form-error">{apiError}</div> : null}
          </div>
        </div>
        <div className="flex justify-between mt-4">
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || createMutation.isPending || uploading}
          >
            {createMutation.isPending ? 'Creating...' : 'Create ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}

function renderLookupHint(
  status: 'idle' | 'loading' | 'found' | 'missing' | 'error',
  message: string | null,
  shopifyAdminUrl: string | null,
  isSalesCategory = false
): React.ReactNode {
  if (status === 'idle') {
    return isSalesCategory
      ? 'Type a draft order ID to autofill customer details from Shopify.'
      : 'Type an order number to autofill customer details from Shopify.';
  }
  if (status === 'loading') return 'Looking up in Shopify...';
  const prefix =
    status === 'found'
      ? ''
      : status === 'missing'
        ? 'Not in Shopify. '
        : 'Shopify error: ';
  return (
    <span>
      {prefix}
      {message}
      {shopifyAdminUrl ? (
        <>
          {' '}·{' '}
          <a href={shopifyAdminUrl} target="_blank" rel="noopener noreferrer">
            Open in Shopify admin
          </a>
        </>
      ) : null}
    </span>
  );
}
